import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import QuestionForm from "./QuestionForm";
import { useSnackbar } from "notistack";
import axios from "axios";
import config from "../../consts";

export interface Question {
  question_text: string;
  question_type: "multiple_choice" | "single_choice";
  points: number;
  answer_options: {
    option_text: string;
    is_correct: boolean;
  }[];
}

export interface QuizData {
  id: number | null;
  title: string;
  description: string;
  passing_score: number;
  questions: Question[];
  created_at: Date;
  status: "draft" | "published";
}

export interface QuestionValidation {
  question_text?: string;
  points?: string;
  answer_options?: string;
  correct_answers?: string;
  answer_option_errors: { option_text?: string }[];
}

export interface QuizValidation {
  title?: string;
  passing_score?: string;
  questions?: string;
  question_errors: QuestionValidation[];
  isValid: boolean;
  totalErrors: number;
}

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.12)",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  padding: theme.spacing(1.25, 3),
  textTransform: "none",
  fontWeight: 600,
}));

const QuizCreatorHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(2, 3),
  borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
}));

const FooterActions = styled(Box)(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  display: "flex",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  padding: theme.spacing(2, 0, 0),
  marginTop: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const createQuestionTemplate = (
  template: "single" | "multiple" | "true_false",
): Question => {
  if (template === "multiple") {
    return {
      question_text: "",
      question_type: "multiple_choice",
      points: 1,
      answer_options: [
        { option_text: "", is_correct: false },
        { option_text: "", is_correct: false },
        { option_text: "", is_correct: false },
        { option_text: "", is_correct: false },
      ],
    };
  }

  if (template === "true_false") {
    return {
      question_text: "True or false?",
      question_type: "single_choice",
      points: 1,
      answer_options: [
        { option_text: "True", is_correct: true },
        { option_text: "False", is_correct: false },
      ],
    };
  }

  return {
    question_text: "",
    question_type: "single_choice",
    points: 1,
    answer_options: [
      { option_text: "", is_correct: false },
      { option_text: "", is_correct: false },
      { option_text: "", is_correct: false },
      { option_text: "", is_correct: false },
    ],
  };
};

const createEmptyQuiz = (): QuizData => ({
  id: null,
  title: "",
  description: "",
  passing_score: 70,
  questions: [],
  created_at: new Date(),
  status: "draft",
});

const getAnswerOptions = (question: Question) =>
  Array.isArray(question.answer_options) ? question.answer_options : [];

const cloneQuestion = (question: Question): Question => ({
  ...question,
  answer_options: getAnswerOptions(question).map((option) => ({ ...option })),
});

const cloneQuiz = (quiz: QuizData): QuizData => ({
  ...quiz,
  created_at: new Date(quiz.created_at),
  questions: Array.isArray(quiz.questions) ? quiz.questions.map(cloneQuestion) : [],
});

const normalizeQuiz = (quiz: QuizData): QuizData => ({
  ...cloneQuiz(quiz),
  status: quiz.status || "draft",
});

const formatDate = (date: Date) => new Date(date).toLocaleDateString();

const extractQuizId = (responseData: any, fallbackId: number | null) => {
  if (responseData?.quiz?.id) {
    return Number(responseData.quiz.id);
  }

  if (responseData?.id) {
    return Number(responseData.id);
  }

  return fallbackId;
};

const getApiErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  (typeof error?.response?.data === "string" ? error.response.data : null) ||
  (error instanceof Error ? error.message : null) ||
  fallback;

const countCorrectAnswers = (question: Question) =>
  getAnswerOptions(question).filter((option) => option.is_correct).length;

const isQuestionResponseCorrect = (
  question: Question,
  selectedOptionIndexes: number[],
) => {
  const correctOptionIndexes = getAnswerOptions(question).reduce<number[]>(
    (indexes, option, index) =>
      option.is_correct ? [...indexes, index] : indexes,
    [],
  );

  if (selectedOptionIndexes.length !== correctOptionIndexes.length) {
    return false;
  }

  return correctOptionIndexes.every((index) =>
    selectedOptionIndexes.includes(index),
  );
};

const validateQuestion = (question: Question): QuestionValidation => {
  const answerOptions = getAnswerOptions(question);
  const answer_option_errors = answerOptions.map((option) => ({
    option_text: String(option.option_text || "").trim()
      ? undefined
      : "Answer choice text is required.",
  }));

  const correctAnswers = answerOptions.filter((option) => option.is_correct).length;

  return {
    question_text: String(question.question_text || "").trim()
      ? undefined
      : "Question text is required.",
    points:
      Number.isFinite(question.points) && question.points >= 1
        ? undefined
        : "Points must be at least 1.",
    answer_options:
      answerOptions.length >= 2
        ? undefined
        : "Add at least two answer choices.",
    correct_answers:
      question.question_type === "single_choice"
        ? correctAnswers === 1
          ? undefined
          : "Single-answer questions need one correct answer."
        : correctAnswers >= 1
          ? undefined
          : "Multiple-answer questions need at least one correct answer.",
    answer_option_errors,
  };
};

const countQuestionErrors = (questionValidation: QuestionValidation) => {
  const messages = [
    questionValidation.question_text,
    questionValidation.points,
    questionValidation.answer_options,
    questionValidation.correct_answers,
    ...questionValidation.answer_option_errors.map((error) => error.option_text),
  ];

  return messages.filter(Boolean).length;
};

export const validateQuiz = (quiz: QuizData): QuizValidation => {
  const quizQuestions = Array.isArray(quiz.questions) ? quiz.questions : [];
  const question_errors = quizQuestions.map(validateQuestion);
  const totalQuestionErrors = question_errors.reduce(
    (sum, questionValidation) => sum + countQuestionErrors(questionValidation),
    0,
  );

  const title = String(quiz.title || "").trim()
    ? undefined
    : "Knowledge check title is required.";
  const passing_score =
    Number.isFinite(quiz.passing_score) &&
    quiz.passing_score >= 1 &&
    quiz.passing_score <= 100
      ? undefined
      : "Passing score must be between 1 and 100.";
  const questionsError =
    quizQuestions.length > 0 ? undefined : "Add at least one question.";

  const totalErrors =
    [title, passing_score, questionsError].filter(Boolean).length +
    totalQuestionErrors;

  return {
    title,
    passing_score,
    questions: questionsError,
    question_errors,
    isValid: totalErrors === 0,
    totalErrors,
  };
};

interface QuizCreatorProps {
  quizData: QuizData;
  setQuizData: React.Dispatch<React.SetStateAction<QuizData>>;
  onSave: (quiz: QuizData) => Promise<void> | void;
  onCancel: () => void;
  embedded?: boolean;
}

function QuizCreator({
  quizData,
  setQuizData,
  onSave,
  onCancel,
  embedded = false,
}: QuizCreatorProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSubmitted, setPreviewSubmitted] = useState(false);
  const [previewResponses, setPreviewResponses] = useState<number[][]>([]);
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(0);
  const [draggedQuestionIndex, setDraggedQuestionIndex] = useState<
    number | null
  >(null);
  const [dragOverQuestionIndex, setDragOverQuestionIndex] = useState<
    number | null
  >(null);

  const validation = useMemo(() => validateQuiz(quizData), [quizData]);
  const canPreviewQuiz =
    quizData.questions.length > 0 &&
    validation.question_errors.every(
      (questionValidation) => countQuestionErrors(questionValidation) === 0,
    );

  const previewResult = useMemo(() => {
    const earnedPoints = quizData.questions.reduce((sum, question, index) => {
      const selectedIndexes = previewResponses[index] || [];
      return sum +
        (isQuestionResponseCorrect(question, selectedIndexes)
          ? question.points
          : 0);
    }, 0);

    const totalPoints = quizData.questions.reduce(
      (sum, question) => sum + question.points,
      0,
    );
    const percent =
      totalPoints === 0 ? 0 : Math.round((earnedPoints / totalPoints) * 100);

    return {
      earnedPoints,
      totalPoints,
      percent,
      passed: percent >= quizData.passing_score,
    };
  }, [previewResponses, quizData]);

  useEffect(() => {
    if (quizData.questions.length === 0) {
      setExpandedQuestion(null);
      return;
    }

    setExpandedQuestion((currentExpanded) => {
      if (currentExpanded === null) {
        return 0;
      }

      return Math.min(currentExpanded, quizData.questions.length - 1);
    });
  }, [quizData.questions.length]);

  const handleQuizFieldChange = (
    field: keyof Pick<QuizData, "title" | "description" | "passing_score">,
    value: string,
  ) => {
    setQuizData((prev) => ({
      ...prev,
      [field]:
        field === "passing_score"
          ? Number.parseInt(value || "0", 10)
          : value,
    }));
  };

  const addQuestion = (
    template: "single" | "multiple" | "true_false" = "single",
  ) => {
    setQuizData((prev) => {
      const nextQuestions = [
        ...prev.questions,
        template === "single"
          ? createQuestionTemplate("single")
          : createQuestionTemplate(template),
      ];
      return {
        ...prev,
        questions: nextQuestions,
      };
    });
    setExpandedQuestion(quizData.questions.length);
  };

  const updateQuestion = (index: number, questionData: Question) => {
    setQuizData((prev) => ({
      ...prev,
      questions: prev.questions.map((question, questionIndex) =>
        questionIndex === index ? questionData : question,
      ),
    }));
  };

  const duplicateQuestion = (index: number) => {
    setQuizData((prev) => {
      const questionToDuplicate = prev.questions[index];
      const nextQuestions = [...prev.questions];
      nextQuestions.splice(index + 1, 0, cloneQuestion(questionToDuplicate));
      return {
        ...prev,
        questions: nextQuestions,
      };
    });
    setExpandedQuestion(index + 1);
  };

  const removeQuestion = (index: number) => {
    setQuizData((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, questionIndex) => questionIndex !== index),
    }));
    setExpandedQuestion((currentExpanded) => {
      if (currentExpanded === null) {
        return null;
      }
      if (currentExpanded === index) {
        return index > 0 ? index - 1 : 0;
      }
      return currentExpanded > index ? currentExpanded - 1 : currentExpanded;
    });
  };

  const moveQuestion = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) {
      return;
    }

    setQuizData((prev) => {
      const nextQuestions = [...prev.questions];
      const [movedQuestion] = nextQuestions.splice(fromIndex, 1);
      nextQuestions.splice(toIndex, 0, movedQuestion);

      return {
        ...prev,
        questions: nextQuestions,
      };
    });
    setExpandedQuestion(toIndex);
  };

  const clearDragState = () => {
    setDraggedQuestionIndex(null);
    setDragOverQuestionIndex(null);
  };

  const openPreview = () => {
    if (!canPreviewQuiz) {
      enqueueSnackbar(
        "Finish the question text and answer choices before previewing the knowledge check.",
        {
          variant: "warning",
        },
      );
      return;
    }

    setPreviewResponses(quizData.questions.map(() => []));
    setPreviewSubmitted(false);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewSubmitted(false);
  };

  const updatePreviewResponse = (
    questionIndex: number,
    optionIndex: number,
    checked: boolean,
  ) => {
    setPreviewResponses((previousResponses) =>
      previousResponses.map((response, currentIndex) => {
        if (currentIndex !== questionIndex) {
          return response;
        }

        const question = quizData.questions[questionIndex];
        if (!question) {
          return response;
        }

        if (question.question_type === "single_choice") {
          return [optionIndex];
        }

        if (checked) {
          return [...response, optionIndex].sort((a, b) => a - b);
        }

        return response.filter((index) => index !== optionIndex);
      }),
    );
  };

  const handlePreviewSubmit = () => {
    setPreviewSubmitted(true);
  };

  const handleSave = async () => {
    setShowValidation(true);

    if (!validation.isValid) {
      enqueueSnackbar("Complete the highlighted knowledge check fields before saving.", {
        variant: "warning",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await Promise.resolve(
        onSave({
          ...cloneQuiz(quizData),
          created_at: quizData.created_at || new Date(),
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const quizStats = {
    questionCount: quizData.questions.length,
    answerCount: quizData.questions.reduce(
      (sum, question) => sum + question.answer_options.length,
      0,
    ),
    correctAnswerCount: quizData.questions.reduce(
      (sum, question) => sum + countCorrectAnswers(question),
      0,
    ),
  };

  const saveButtonLabel = quizData.id ? "Save Changes" : "Save Knowledge Check";

  return (
    <Container
      maxWidth={embedded ? false : "lg"}
      disableGutters={embedded}
      sx={{ py: embedded ? 0 : 3 }}
    >
      <StyledCard>
        <QuizCreatorHeader>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton
              onClick={onCancel}
              sx={{ color: "inherit" }}
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" component="h2">
                {quizData.id ? "Edit Knowledge Check" : "Build Knowledge Check"}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Keep this short and focused so learners can confirm they
                understood the notice.
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            <Chip
              label={`${quizStats.questionCount} question${
                quizStats.questionCount === 1 ? "" : "s"
              }`}
              color="default"
              sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "inherit" }}
            />
            <Chip
              label={`${quizData.passing_score || 0}% required`}
              color="default"
              sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "inherit" }}
            />
          </Stack>
        </QuizCreatorHeader>

        <CardContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            {!validation.isValid && showValidation && (
              <Alert severity="warning">
                This knowledge check still has {validation.totalErrors} field
                {validation.totalErrors === 1 ? "" : "s"} to fix before it can
                be saved.
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Knowledge Check Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add a learner-friendly title, optional instructions, and
                    the score needed to pass.
                  </Typography>
                </Box>

                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Builder Summary
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                      <Chip label={`${quizStats.answerCount} choices`} size="small" />
                      <Chip
                        label={`${quizStats.correctAnswerCount} correct answers`}
                        size="small"
                        color={
                          quizStats.correctAnswerCount > 0 ? "success" : "default"
                        }
                      />
                      <Chip
                        label={
                          quizData.status === "published"
                            ? "Published"
                            : "Draft"
                        }
                        size="small"
                        color={
                          quizData.status === "published" ? "success" : "default"
                        }
                      />
                    </Stack>
                  </Box>
                </Stack>

                <Stack spacing={2}>
                  <TextField
                    label="Knowledge Check Title"
                    value={quizData.title}
                    onChange={(event) =>
                      handleQuizFieldChange("title", event.target.value)
                    }
                    placeholder="e.g. Toolbox safety check"
                    fullWidth
                    required
                    error={Boolean(validation.title)}
                    helperText={
                      validation.title ||
                      "Use a short title learners can scan quickly."
                    }
                  />

                  <TextField
                    label="Learner Instructions"
                    value={quizData.description}
                    onChange={(event) =>
                      handleQuizFieldChange("description", event.target.value)
                    }
                    placeholder="Add a short learning goal or instruction"
                    fullWidth
                    multiline
                    minRows={4}
                  />

                  <TextField
                    label="Score Needed to Pass"
                    type="number"
                    value={quizData.passing_score}
                    onChange={(event) =>
                      handleQuizFieldChange("passing_score", event.target.value)
                    }
                    inputProps={{ min: 1, max: 100 }}
                    error={Boolean(validation.passing_score)}
                    helperText={
                      validation.passing_score ||
                      "Enter a percentage between 1 and 100."
                    }
                    sx={{ width: { xs: "100%", sm: 200 } }}
                  />
                </Stack>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Stack spacing={2.5}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography variant="h6">Build Learner Questions</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add the questions learners need to answer after reading
                      the notice.
                    </Typography>
                  </Box>
                  <StyledButton
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => addQuestion("single")}
                  >
                    Add Question
                  </StyledButton>
                </Box>

                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => addQuestion("single")}
                  >
                    Single-answer Question
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => addQuestion("multiple")}
                  >
                    Multiple-answer Question
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => addQuestion("true_false")}
                  >
                    True / False Question
                  </Button>
                </Stack>

                {validation.questions && showValidation && (
                  <Alert severity="warning">{validation.questions}</Alert>
                )}

                {quizData.questions.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 4,
                      borderStyle: "dashed",
                      textAlign: "center",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      No questions yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Start with one question. You can add more if the notice
                      needs a stronger check.
                    </Typography>
                    <StyledButton
                      variant="outlined"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => addQuestion("single")}
                    >
                      Add First Question
                    </StyledButton>
                  </Paper>
                ) : (
                  <Stack spacing={2}>
                    {quizData.questions.map((question, index) => {
                      const questionValidation = validation.question_errors[index];
                      const questionErrorCount = countQuestionErrors(
                        questionValidation,
                      );
                      const correctAnswerCount = countCorrectAnswers(question);
                      const isExpanded = expandedQuestion === index;
                      const isDragOver = dragOverQuestionIndex === index;

                      return (
                        <Accordion
                          key={`question-${index}`}
                          expanded={isExpanded}
                          onChange={(_, expanded) =>
                            setExpandedQuestion(expanded ? index : null)
                          }
                          disableGutters
                          variant="outlined"
                          draggable
                          onDragStart={() => setDraggedQuestionIndex(index)}
                          onDragOver={(event) => {
                            event.preventDefault();
                            if (draggedQuestionIndex !== index) {
                              setDragOverQuestionIndex(index);
                            }
                          }}
                          onDrop={(event) => {
                            event.preventDefault();
                            if (
                              draggedQuestionIndex !== null &&
                              draggedQuestionIndex !== index
                            ) {
                              moveQuestion(draggedQuestionIndex, index);
                            }
                            clearDragState();
                          }}
                          onDragEnd={clearDragState}
                          sx={{
                            borderRadius: 2,
                            borderColor: isDragOver ? "primary.main" : "divider",
                            boxShadow: isExpanded
                              ? "0 10px 24px rgba(0,0,0,0.08)"
                              : "none",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              "& .MuiAccordionSummary-content": {
                                alignItems: "center",
                                gap: 1.5,
                                flexWrap: "wrap",
                              },
                            }}
                          >
                            <DragIndicatorIcon color="action" />
                            <Box sx={{ minWidth: 140 }}>
                              <Typography variant="subtitle1" fontWeight={600}>
                                Question {index + 1}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {question.question_text.trim() || "Untitled question"}
                              </Typography>
                            </Box>

                            <Chip
                              size="small"
                              label={
                                question.question_type === "single_choice"
                                  ? "Single answer"
                                  : "Multiple answers"
                              }
                            />
                            <Chip
                              size="small"
                              label={`${question.points} pt${
                                question.points === 1 ? "" : "s"
                              }`}
                            />
                            <Chip
                              size="small"
                              color={
                                correctAnswerCount > 0 ? "success" : "default"
                              }
                              label={`${correctAnswerCount} correct`}
                            />
                            {questionErrorCount > 0 && showValidation && (
                              <Chip
                                size="small"
                                color="error"
                                label={`${questionErrorCount} issue${
                                  questionErrorCount === 1 ? "" : "s"
                                }`}
                              />
                            )}

                            <Box
                              sx={{
                                ml: "auto",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <Tooltip title="Move up">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveQuestion(index, index - 1)}
                                    disabled={index === 0}
                                  >
                                    <ArrowUpwardIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Move down">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveQuestion(index, index + 1)}
                                    disabled={index === quizData.questions.length - 1}
                                  >
                                    <ArrowDownwardIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Duplicate question">
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateQuestion(index)}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete question">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => removeQuestion(index)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails>
                            <QuestionForm
                              questionData={question}
                              validation={showValidation ? questionValidation : undefined}
                              onUpdate={(questionData) =>
                                updateQuestion(index, questionData)
                              }
                            />
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6">Review Knowledge Check</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Check the learner-facing questions before adding this to
                    the notice.
                  </Typography>
                </Box>

                <Stack spacing={1.5}>
                  {quizData.questions.length === 0 ? (
                    <Typography color="text.secondary">
                      Questions will appear here once you add them.
                    </Typography>
                  ) : (
                    quizData.questions.map((question, index) => (
                      <Paper
                        key={`question-preview-${index}`}
                        variant="outlined"
                        sx={{ p: 2, borderRadius: 2 }}
                      >
                        <Stack spacing={1}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 2,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography fontWeight={600}>
                              {index + 1}.{" "}
                              {question.question_text.trim() || "Untitled question"}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <Chip
                                size="small"
                                label={
                                  question.question_type === "single_choice"
                                    ? "Single answer"
                                    : "Multiple answers"
                                }
                              />
                              <Chip
                                size="small"
                                label={`${question.points} pt${
                                  question.points === 1 ? "" : "s"
                                }`}
                              />
                            </Stack>
                          </Box>

                          <Stack spacing={0.5}>
                            {question.answer_options.map((option, optionIndex) => (
                              <Box
                                key={`preview-option-${optionIndex}`}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  color: option.is_correct
                                    ? "success.main"
                                    : "text.secondary",
                                }}
                              >
                                <Typography variant="body2">
                                  {option.is_correct ? "Correct answer:" : "Answer choice:"}
                                </Typography>
                                <Typography variant="body2">
                                  {option.option_text || `Choice ${optionIndex + 1}`}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Stack>
                      </Paper>
                    ))
                  )}
                </Stack>
              </Stack>
            </Paper>

            <FooterActions>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                <StyledButton
                  variant="outlined"
                  color="primary"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </StyledButton>
                <StyledButton
                  variant="outlined"
                  color="primary"
                  startIcon={<VisibilityOutlinedIcon />}
                  onClick={openPreview}
                  disabled={isSubmitting}
                >
                  Preview Check
                </StyledButton>
              </Stack>
              <StyledButton
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : saveButtonLabel}
              </StyledButton>
            </FooterActions>

            <Dialog
              open={previewOpen}
              onClose={closePreview}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Knowledge Check Preview</DialogTitle>
              <DialogContent>
                <Stack spacing={3} sx={{ pt: 1 }}>
                  <Typography color="text.secondary">
                    This shows what learners will answer without saving any
                    responses.
                  </Typography>

                  {quizData.questions.map((question, questionIndex) => (
                    <Paper
                      key={`preview-question-${questionIndex}`}
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 2 }}
                    >
                      <Stack spacing={1.5}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography fontWeight={600}>
                            {questionIndex + 1}. {question.question_text}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${question.points} pt${
                              question.points === 1 ? "" : "s"
                            }`}
                          />
                        </Box>

                        {question.question_type === "single_choice" ? (
                          <RadioGroup
                            value={String(previewResponses[questionIndex]?.[0] ?? "")}
                            onChange={(event) =>
                              updatePreviewResponse(
                                questionIndex,
                                Number(event.target.value),
                                true,
                              )
                            }
                          >
                            {question.answer_options.map((option, optionIndex) => (
                              <FormControlLabel
                                key={`preview-single-${questionIndex}-${optionIndex}`}
                                value={String(optionIndex)}
                                control={<Radio />}
                                label={option.option_text}
                              />
                            ))}
                          </RadioGroup>
                        ) : (
                          <Stack>
                            {question.answer_options.map((option, optionIndex) => (
                              <FormControlLabel
                                key={`preview-multiple-${questionIndex}-${optionIndex}`}
                                control={
                                  <Checkbox
                                    checked={(
                                      previewResponses[questionIndex] || []
                                    ).includes(optionIndex)}
                                    onChange={(event) =>
                                      updatePreviewResponse(
                                        questionIndex,
                                        optionIndex,
                                        event.target.checked,
                                      )
                                    }
                                  />
                                }
                                label={option.option_text}
                              />
                            ))}
                          </Stack>
                        )}

                        {previewSubmitted && (
                          <Alert
                            severity={
                              isQuestionResponseCorrect(
                                question,
                                previewResponses[questionIndex] || [],
                              )
                                ? "success"
                                : "warning"
                            }
                          >
                            {isQuestionResponseCorrect(
                              question,
                              previewResponses[questionIndex] || [],
                            )
                              ? "Correct in preview."
                              : "Not correct in preview."}
                          </Alert>
                        )}
                      </Stack>
                    </Paper>
                  ))}

                  {previewSubmitted && (
                    <Alert
                      severity={previewResult.passed ? "success" : "warning"}
                    >
                      Preview score: {previewResult.earnedPoints} /{" "}
                      {previewResult.totalPoints} points ({previewResult.percent}
                      %). {previewResult.passed ? "Passing." : "Not passing yet."}
                    </Alert>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={closePreview}>Close</Button>
                <Button variant="contained" onClick={handlePreviewSubmit}>
                  {previewSubmitted ? "Check Again" : "Check Answers"}
                </Button>
              </DialogActions>
            </Dialog>
          </Stack>
        </CardContent>
      </StyledCard>
    </Container>
  );
}

interface MultiQuizManagerProps {
  quizzes: QuizData[];
  noticeCreationSetQuizzes: React.Dispatch<React.SetStateAction<QuizData[]>> | null;
  editMode: boolean;
  editModeNoticeId: number | null;
  embedded?: boolean;
  onEditingStateChange?: (isEditing: boolean) => void;
}

export default function MultiQuizManager({
  quizzes,
  noticeCreationSetQuizzes,
  editMode,
  editModeNoticeId,
  embedded = false,
  onEditingStateChange,
}: MultiQuizManagerProps) {
  const [currentQuiz, setCurrentQuiz] = useState<QuizData | null>(null);
  const [editingQuizIndex, setEditingQuizIndex] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quizToDeleteIndex, setQuizToDeleteIndex] = useState<number | null>(null);
  const [isSavingQuiz, setIsSavingQuiz] = useState(false);
  const [isDeletingQuiz, setIsDeletingQuiz] = useState(false);
  const [expandedQuizIndex, setExpandedQuizIndex] = useState<number | false>(0);
  const [localQuizzes, setLocalQuizzes] = useState<QuizData[]>(
    quizzes.map(normalizeQuiz),
  );
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setLocalQuizzes(quizzes.map(normalizeQuiz));
  }, [quizzes]);

  useEffect(() => {
    onEditingStateChange?.(Boolean(currentQuiz));
  }, [currentQuiz, onEditingStateChange]);

  const updateDraftQuizzes = (nextQuizzes: QuizData[]) => {
    setLocalQuizzes(nextQuizzes);
    noticeCreationSetQuizzes?.(nextQuizzes);
  };

  const handleCreateNew = () => {
    setEditingQuizIndex(null);
    setCurrentQuiz(createEmptyQuiz());
  };

  const handleEditQuiz = (quiz: QuizData, index: number) => {
    setEditingQuizIndex(index);
    setCurrentQuiz(cloneQuiz(quiz));
  };

  const handleDuplicateQuiz = (quiz: QuizData) => {
    setEditingQuizIndex(null);
    setCurrentQuiz({
      ...cloneQuiz(quiz),
      id: null,
      title: quiz.title ? `${quiz.title} (Copy)` : "Untitled Knowledge Check (Copy)",
      created_at: new Date(),
      status: editMode ? "published" : "draft",
    });
  };

  const handleSaveQuiz = async (quiz: QuizData) => {
    const normalizedQuiz = cloneQuiz({
      ...quiz,
      created_at: quiz.created_at || new Date(),
    });

    if (!editMode) {
      const nextQuizzes =
        editingQuizIndex === null
          ? [...localQuizzes, { ...normalizedQuiz, status: "draft" }]
          : localQuizzes.map((existingQuiz, index) =>
              index === editingQuizIndex
                ? { ...normalizedQuiz, status: "draft" }
                : existingQuiz,
            );

      updateDraftQuizzes(nextQuizzes);
      enqueueSnackbar(
        editingQuizIndex === null
          ? "Knowledge check added to this notice draft."
          : "Knowledge check draft updated.",
        {
          variant: "success",
        },
      );
      setCurrentQuiz(null);
      setEditingQuizIndex(null);
      return;
    }

    if (!editModeNoticeId) {
      enqueueSnackbar("No notice selected for knowledge check editing.", {
        variant: "error",
      });
      return;
    }

    setIsSavingQuiz(true);
    try {
      const payload = {
        ...normalizedQuiz,
        notice_id: editModeNoticeId,
        status: "published" as const,
      };
      let savedQuizId = normalizedQuiz.id;

      if (normalizedQuiz.id) {
        const response = await axios.patch(
          `${config.api}/v2/cig/quizzes/patch`,
          payload,
        );
        savedQuizId = extractQuizId(response.data, normalizedQuiz.id);
      } else {
        const response = await axios.post(
          `${config.api}/v2/cig/quizzes/create`,
          payload,
        );
        savedQuizId = extractQuizId(response.data, normalizedQuiz.id);
      }

      const nextQuiz = {
        ...normalizedQuiz,
        id: savedQuizId,
        status: "published" as const,
      };

      setLocalQuizzes((previousQuizzes) =>
        editingQuizIndex === null
          ? [...previousQuizzes, nextQuiz]
          : previousQuizzes.map((existingQuiz, index) =>
              index === editingQuizIndex ? nextQuiz : existingQuiz,
            ),
      );

      enqueueSnackbar(
        normalizedQuiz.id
          ? "Knowledge check updated."
          : "Knowledge check created.",
        {
          variant: "success",
        },
      );
      setCurrentQuiz(null);
      setEditingQuizIndex(null);
    } catch (error) {
      console.error("Error saving quiz:", error);
      enqueueSnackbar(
        getApiErrorMessage(error, "Failed to save the knowledge check. Please try again."),
        {
          variant: "error",
        },
      );
    } finally {
      setIsSavingQuiz(false);
    }
  };

  const handleDeleteQuiz = (index: number) => {
    setQuizToDeleteIndex(index);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (quizToDeleteIndex === null) {
      return;
    }

    const selectedQuiz = localQuizzes[quizToDeleteIndex];
    if (!selectedQuiz) {
      setDeleteDialogOpen(false);
      setQuizToDeleteIndex(null);
      return;
    }

    if (!selectedQuiz.id || !editMode) {
      const nextQuizzes = localQuizzes.filter(
        (_, index) => index !== quizToDeleteIndex,
      );
      updateDraftQuizzes(nextQuizzes);
      enqueueSnackbar("Knowledge check removed from this notice draft.", {
        variant: "success",
      });
      setDeleteDialogOpen(false);
      setQuizToDeleteIndex(null);
      return;
    }

    setIsDeletingQuiz(true);
    try {
      await axios.delete(
        `${config.api}/v2/cig/quizzes/delete/${selectedQuiz.id}`,
      );
      setLocalQuizzes((previousQuizzes) =>
        previousQuizzes.filter((_, index) => index !== quizToDeleteIndex),
      );
      enqueueSnackbar("Knowledge check deleted.", { variant: "success" });
      setDeleteDialogOpen(false);
      setQuizToDeleteIndex(null);
    } catch (error) {
      console.error("Error deleting quiz:", error);
      enqueueSnackbar(
        getApiErrorMessage(error, "Failed to delete the knowledge check. Please try again."),
        {
          variant: "error",
        },
      );
    } finally {
      setIsDeletingQuiz(false);
    }
  };

  const handleCancel = () => {
    setCurrentQuiz(null);
    setEditingQuizIndex(null);
  };

  if (currentQuiz) {
    return (
      <QuizCreator
        quizData={currentQuiz}
        setQuizData={(updater) => {
          if (typeof updater === "function") {
            setCurrentQuiz((previousQuiz) =>
              previousQuiz ? updater(previousQuiz) : previousQuiz,
            );
          } else {
            setCurrentQuiz(updater);
          }
        }}
        onSave={handleSaveQuiz}
        onCancel={handleCancel}
        embedded={embedded}
      />
    );
  }

  return (
    <Container
      maxWidth={embedded ? false : "lg"}
      disableGutters={embedded}
      sx={{ py: embedded ? 0 : 4 }}
    >
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography variant="h6">
                {editMode ? "Manage Knowledge Checks" : "Add Knowledge Checks"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {editMode
                  ? "Create, edit, and remove checks attached to this learning notice."
                  : "Add optional checks now. They stay in draft until the notice is published."}
              </Typography>
            </Box>
            <StyledButton
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              disabled={isSavingQuiz}
            >
              Create Knowledge Check
            </StyledButton>
          </Box>
        </Paper>

        {!editMode && (
          <Alert severity="info">
            Knowledge checks are kept inside the notice draft. They will only be
            published when you publish the notice.
          </Alert>
        )}

        {localQuizzes.length === 0 ? (
          <StyledCard>
            <CardContent>
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No knowledge checks added yet
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Start with one check and add more only when the notice needs
                  a stronger learning record.
                </Typography>
                <StyledButton
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleCreateNew}
                >
                  Add First Knowledge Check
                </StyledButton>
              </Box>
            </CardContent>
          </StyledCard>
        ) : (
          <Stack spacing={2}>
            {localQuizzes.map((quiz, index) => (
              <Accordion
                key={`quiz-${quiz.id ?? index}`}
                expanded={expandedQuizIndex === index}
                onChange={(_, expanded) =>
                  setExpandedQuizIndex(expanded ? index : false)
                }
                variant="outlined"
                disableGutters
                sx={{ borderRadius: 2 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      gap: 1.5,
                      flexWrap: "wrap",
                    },
                  }}
                >
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {quiz.title || `Knowledge check ${index + 1}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {quiz.description || "No learner instructions added yet."}
                    </Typography>
                  </Box>

                  <Chip
                    size="small"
                    label={`${quiz.questions.length} question${
                      quiz.questions.length === 1 ? "" : "s"
                    }`}
                  />
                  <Chip
                    size="small"
                    label={`${quiz.passing_score}% required`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={quiz.status === "published" ? "Published" : "Draft"}
                    color={quiz.status === "published" ? "success" : "default"}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Created ${formatDate(quiz.created_at)}`}
                  />

                  <Box
                    sx={{
                      ml: "auto",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Tooltip title="Edit knowledge check">
                      <IconButton
                        size="small"
                        onClick={() => handleEditQuiz(quiz, index)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate knowledge check">
                      <IconButton
                        size="small"
                        onClick={() => handleDuplicateQuiz(quiz)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete knowledge check">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteQuiz(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Stack spacing={1.5}>
                    {quiz.questions.map((question, questionIndex) => (
                      <Paper
                        key={`quiz-${index}-question-${questionIndex}`}
                        variant="outlined"
                        sx={{ p: 2, borderRadius: 2 }}
                      >
                        <Typography fontWeight={600} gutterBottom>
                          {questionIndex + 1}.{" "}
                          {question.question_text || "Untitled question"}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
                          <Chip
                            size="small"
                            label={
                              question.question_type === "single_choice"
                                ? "Single answer"
                                : "Multiple answers"
                            }
                          />
                          <Chip
                            size="small"
                            label={`${question.points} pt${
                              question.points === 1 ? "" : "s"
                            }`}
                          />
                          <Chip
                            size="small"
                            color={
                              countCorrectAnswers(question) > 0
                                ? "success"
                                : "default"
                            }
                            label={`${countCorrectAnswers(question)} correct`}
                          />
                        </Stack>
                        <Stack spacing={0.5}>
                          {question.answer_options.map((option, optionIndex) => (
                            <Typography
                              key={`quiz-${index}-question-${questionIndex}-option-${optionIndex}`}
                              variant="body2"
                              color={
                                option.is_correct
                                  ? "success.main"
                                  : "text.secondary"
                              }
                            >
                              {option.is_correct ? "Correct answer:" : "Answer choice:"}{" "}
                              {option.option_text || `Choice ${optionIndex + 1}`}
                            </Typography>
                          ))}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </Stack>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Knowledge Check</DialogTitle>
        <DialogContent>
          <Typography>
            Delete this knowledge check? Learners will no longer see it with
            this notice.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeletingQuiz}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={isDeletingQuiz}
          >
            {isDeletingQuiz ? "Deleting..." : "Delete Check"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
